
import { useState } from "react";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { candidatesAPI } from "@/services/api";
import { useQuery } from "@tanstack/react-query";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { Eye, FileText, ListChecks, Download } from "lucide-react";

const CompanyCandidates = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { data: candidatesData, isLoading } = useQuery({
    queryKey: ["companyCandidates", currentPage, searchTerm],
    queryFn: () => candidatesAPI.getAll({
      page: currentPage,
      limit: itemsPerPage,
      search: searchTerm
    }),
    select: (res) => res.data,
    keepPreviousData: true,
  });

  const candidates = candidatesData?.candidates || [];
  const totalPages = candidatesData?.totalPages || 1;

  const performanceData = candidates.map(candidate => ({
    name: candidate.name.substring(0, 10) + (candidate.name.length > 10 ? '...' : ''),
    score: candidate.examHistory && candidate.examHistory.length > 0 
      ? Math.round(Math.random() * 100) // This would be replaced with actual data
      : 0,
    accuracy: candidate.examHistory && candidate.examHistory.length > 0 
      ? Math.round(Math.random() * 100) // This would be replaced with actual data
      : 0,
    timeSpent: candidate.examHistory && candidate.examHistory.length > 0 
      ? Math.round(Math.random() * 60) // This would be replaced with actual data
      : 0,
  }));

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Company Candidates</h1>
        <p className="text-muted-foreground">
          Overview and performance of all candidates in your company
        </p>
      </div>

      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Candidates</TabsTrigger>
          <TabsTrigger value="performance">Performance Analysis</TabsTrigger>
          <TabsTrigger value="export">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Candidates List</CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search candidates..."
                  className="w-64"
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </div>
            </CardHeader>
            <CardContent>
              {candidates.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No candidates found. Add candidates to see them here.</p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Assigned Exam</TableHead>
                        <TableHead>Assigned By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {candidates.map((candidate) => (
                        <TableRow key={candidate._id}>
                          <TableCell>{candidate.name}</TableCell>
                          <TableCell>{candidate.email}</TableCell>
                          <TableCell>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                              ${candidate.status === 'completed' ? 'bg-green-100 text-green-800' : 
                                candidate.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                                candidate.status === 'disqualified' ? 'bg-red-100 text-red-800' :
                                'bg-yellow-100 text-yellow-800'}
                            `}>
                              {candidate.status.charAt(0).toUpperCase() + candidate.status.slice(1)}
                            </span>
                          </TableCell>
                          <TableCell>{candidate.examId?.name || "N/A"}</TableCell>
                          <TableCell>{candidate.assignedBy?.name || "N/A"}</TableCell>
                          <TableCell>
                            <div className="flex space-x-2">
                              <Button variant="outline" size="sm">
                                <Eye className="h-4 w-4 mr-1" />
                                View
                              </Button>
                              <Button variant="outline" size="sm">
                                <FileText className="h-4 w-4 mr-1" />
                                Results
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between space-x-2 py-4">
                    <div className="text-sm text-muted-foreground">
                      Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span>
                      {" "} - {" "}
                      <span className="font-medium">
                        {Math.min(currentPage * itemsPerPage, candidatesData?.total || 0)}
                      </span>
                      {" "} of {" "}
                      <span className="font-medium">{candidatesData?.total || 0}</span> entries
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrevPage}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleNextPage}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader>
              <CardTitle>Candidate Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={performanceData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="score" fill="#8884d8" name="Score (%)" />
                    <Bar dataKey="accuracy" fill="#82ca9d" name="Accuracy (%)" />
                    <Bar dataKey="timeSpent" fill="#ffc658" name="Time Spent (min)" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              
              <div className="mt-8">
                <h3 className="text-lg font-medium mb-4">Top Performers</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Candidate</TableHead>
                      <TableHead>Exam</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Accuracy</TableHead>
                      <TableHead>Time Spent</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {performanceData
                      .sort((a, b) => b.score - a.score)
                      .slice(0, 5)
                      .map((data, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {candidates[index]?.name || `Candidate ${index + 1}`}
                          </TableCell>
                          <TableCell>
                            {candidates[index]?.examId?.name || "N/A"}
                          </TableCell>
                          <TableCell>{data.score}%</TableCell>
                          <TableCell>{data.accuracy}%</TableCell>
                          <TableCell>{data.timeSpent} min</TableCell>
                          <TableCell>
                            <Button variant="outline" size="sm">
                              <ListChecks className="h-4 w-4 mr-1" />
                              Details
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="export">
          <Card>
            <CardHeader>
              <CardTitle>Generate Reports</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Overall Performance Report</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Comprehensive report showing all candidate performances and analytics for your company.
                    </p>
                    <Button className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Export as PDF
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Candidate Detail Reports</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Individual reports for each candidate showing their exam details and results.
                    </p>
                    <Button className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Export as Excel
                    </Button>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Exam Analytics</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground mb-4">
                      Detailed analysis of all exams conducted, question performance, and results.
                    </p>
                    <Button className="w-full">
                      <Download className="h-4 w-4 mr-2" />
                      Export as CSV
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CompanyCandidates;
